import { BillingType, CaseChartsHoursRole, FinancialTimelineCategory, UserType } from '../../../../../generated'
import palette from '../../../../theme/palette'

export const CHART_HEIGHT = 320

/**
 * Chart palette built around Helmer's primary red. `red` is the brand red lifted one step: the brand
 * value itself (#771117) is too dark to tell apart from neighbouring marks. Every pairing below was
 * checked for color-blind separation; `neutral` is the deliberately colorless "remaining / not
 * charged" share, and always sits next to a value label or legend entry.
 */
export const CHART_PALETTE = {
  red: '#9E1C26',
  rose: '#D9727F',
  amber: '#B5772A',
  plum: '#7A3E6B',
  terracotta: '#C4562E',
  neutral: palette.lightGrayFive,
  // Lighter than `neutral` so it stays distinct from plum when they sit side by side
  neutralLight: '#98A2B3'
}

/** The same metric is always drawn in the same color wherever it appears on the tab. */
export const CHART_COLORS = {
  contract: CHART_PALETTE.amber,
  paid: CHART_PALETTE.red,
  due: CHART_PALETTE.rose,
  residual: CHART_PALETTE.neutral,
  consumed: CHART_PALETTE.plum,
  // Contract Fee Summary (Flat Fee): red "paid" sits between these, so they avoid red-adjacent hues
  summaryContract: CHART_PALETTE.plum,
  summaryPaidToDate: CHART_PALETTE.amber,
  summaryAr: CHART_PALETTE.rose,
  // Financial Summary groups
  received: CHART_PALETTE.red,
  charged: CHART_PALETTE.amber,
  held: CHART_PALETTE.plum,
  writtenOff: CHART_PALETTE.neutralLight,
  // Hours by Billing Status
  hoursTotal: CHART_PALETTE.amber,
  hoursBilled: CHART_PALETTE.red,
  hoursUnbilled: CHART_PALETTE.rose,
  hoursOnPreBill: CHART_PALETTE.plum,
  hoursWip: CHART_PALETTE.terracotta,
  hoursAccommodated: CHART_PALETTE.neutral
}

export const ROLE_COLORS: Record<CaseChartsHoursRole, string> = {
  [CaseChartsHoursRole.Attorney]: CHART_PALETTE.red,
  [CaseChartsHoursRole.Paralegal]: CHART_PALETTE.rose,
  [CaseChartsHoursRole.Investigator]: CHART_PALETTE.plum,
  [CaseChartsHoursRole.Other]: CHART_PALETTE.amber
}

/** Legal team seats, most senior first, with how each is labelled and colored on the Hours by Legal Team chart. */
export const LEGAL_TEAM_POSITIONS: { position: UserType; label: string; color: string }[] = [
  { position: UserType.ResponsibleAttorney_1, label: 'Responsible Attorney 1', color: CHART_PALETTE.red },
  { position: UserType.ResponsibleAttorney_2, label: 'Responsible Attorney 2', color: CHART_PALETTE.red },
  { position: UserType.ResponsibleAttorney_3, label: 'Responsible Attorney 3', color: CHART_PALETTE.red },
  { position: UserType.ResponsibleAttorney_4, label: 'Responsible Attorney 4', color: CHART_PALETTE.red },
  { position: UserType.ResponsibleAttorney_5, label: 'Responsible Attorney 5', color: CHART_PALETTE.red },
  { position: UserType.ResponsibleAttorney_6, label: 'Responsible Attorney 6', color: CHART_PALETTE.red },
  { position: UserType.OriginatorAttorney, label: 'Originator Attorney', color: CHART_PALETTE.red },
  { position: UserType.SupervisingAttorney, label: 'Supervising Attorney', color: CHART_PALETTE.red },
  { position: UserType.Attorney, label: 'Attorney', color: CHART_PALETTE.red },
  { position: UserType.Paralegal, label: 'Paralegal', color: CHART_PALETTE.rose },
  { position: UserType.Investigator, label: 'Investigator', color: CHART_PALETTE.plum }
]

export const NOT_ON_LEGAL_TEAM = { label: 'Not on Legal Team', color: CHART_PALETTE.neutralLight }

export const LEGAL_TEAM_LEGEND = [
  { label: 'Attorneys', color: CHART_PALETTE.red },
  { label: 'Paralegal', color: CHART_PALETTE.rose },
  { label: 'Investigator', color: CHART_PALETTE.plum },
  NOT_ON_LEGAL_TEAM
]

export const ROLE_LABELS: Record<CaseChartsHoursRole, string> = {
  [CaseChartsHoursRole.Attorney]: 'Attorney',
  [CaseChartsHoursRole.Paralegal]: 'Paralegal',
  [CaseChartsHoursRole.Investigator]: 'Investigator',
  [CaseChartsHoursRole.Other]: 'Other'
}

/** Timeline lanes, top to bottom. Payments wear the same red as "paid" elsewhere on the tab. */
export const TIMELINE_CATEGORIES: { category: FinancialTimelineCategory; label: string; color: string }[] = [
  { category: FinancialTimelineCategory.Matter, label: 'Matter', color: CHART_PALETTE.neutral },
  { category: FinancialTimelineCategory.Bill, label: 'Bills', color: CHART_PALETTE.amber },
  { category: FinancialTimelineCategory.Payment, label: 'Payments', color: CHART_PALETTE.red },
  { category: FinancialTimelineCategory.Refund, label: 'Refunds', color: CHART_PALETTE.rose },
  { category: FinancialTimelineCategory.Payable, label: 'Payables', color: CHART_PALETTE.plum }
]

export const formatHours = (value: number) => `${value.toFixed(2)} hrs`
export const formatAxisHours = (value: number) => `${value}h`

/** Billing types that have at least one chart; the tab is hidden for every other type. */
export const CHART_BILLING_TYPES: BillingType[] = [BillingType.FlatFee, BillingType.Hourly, BillingType.Hybrid]

export const formatAxisCurrency = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`
  return `$${value}`
}
